import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  articlesService,
  categoriesService,
  subcategoriesService,
} from "@/lib/supabase";
import { Article } from "./use-articles";

export interface ArticleDetail extends Article {
  content: string;
  authorTitle?: string;
  authorBio?: string;
  authorImage?: string;
}

export interface RelatedArticle {
  id: string;
  title: string;
  excerpt: string;
  imageUrl: string;
}

export function useArticleDetails() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<RelatedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset states when id changes
    setLoading(true);
    setError(null);

    const fetchArticleDetails = async () => {
      try {
        if (!id) {
          throw new Error("Article ID not provided");
        }

        // Fetch article details from Supabase
        const articleData = await articlesService.getById(id);

        if (!articleData) {
          throw new Error("Article not found");
        }

        // Fetch categories and subcategories for proper naming
        const categories = await categoriesService.getAll();
        const subcategories = await subcategoriesService.getAll();

        // Find the category and subcategory objects
        const category = categories.find(
          (c) => c.id === articleData.category_id
        );
        const subcategory = articleData.subcategory_id
          ? subcategories.find((sc) => sc.id === articleData.subcategory_id)
          : undefined;

        // Map the article details to our UI format
        const articleDetail: ArticleDetail = {
          id: articleData.id,
          title: articleData.title,
          excerpt: articleData.excerpt,
          content: articleData.content,
          category: category?.name || "Không xác định",
          subCategory: subcategory?.name,
          categoryId: articleData.category_id,
          subCategoryId: articleData.subcategory_id,
          author: articleData.author,
          authorTitle: articleData.author_title,
          authorBio: articleData.author_bio,
          authorImage: articleData.author_image,
          date: new Date(articleData.date).toLocaleDateString("vi-VN", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
          readTime: articleData.read_time,
          imageUrl: articleData.image_url,
        };

        setArticle(articleDetail);

        // Fetch related articles (articles in the same category or subcategory)
        let relatedArticlesData;

        if (articleData.subcategory_id) {
          // First try to get articles from the same subcategory
          relatedArticlesData = await articlesService.getBySubcategory(
            articleData.subcategory_id
          );

          // Filter out the current article
          relatedArticlesData = relatedArticlesData.filter((a) => a.id !== id);

          // If we don't have enough related articles, get some from the same category
          if (relatedArticlesData.length < 3) {
            const categoryArticles = await articlesService.getByCategory(
              articleData.category_id
            );

            // Add only the ones we don't already have
            const additionalArticles = categoryArticles.filter(
              (a) =>
                a.id !== id && !relatedArticlesData.some((ra) => ra.id === a.id)
            );

            // Combine the results
            relatedArticlesData = [
              ...relatedArticlesData,
              ...additionalArticles,
            ].slice(0, 3);
          }
        } else {
          // If no subcategory, just get articles from the same category
          relatedArticlesData = await articlesService.getByCategory(
            articleData.category_id
          );

          // Filter out the current article
          relatedArticlesData = relatedArticlesData
            .filter((a) => a.id !== id)
            .slice(0, 3);
        }

        // Map related articles to the UI format
        const mappedRelatedArticles: RelatedArticle[] = relatedArticlesData.map(
          (ra) => ({
            id: ra.id,
            title: ra.title,
            excerpt: ra.excerpt,
            imageUrl: ra.image_url,
          })
        );

        setRelatedArticles(mappedRelatedArticles);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching article details:", err);
        setError(err instanceof Error ? err.message : "Unknown error occurred");
        setLoading(false);
      }
    };

    fetchArticleDetails();
  }, [id]);

  return {
    article,
    relatedArticles,
    loading,
    error,
  };
}
